// Renders the Sentinel workspace and wires its actions.

import { SentinelTicketSections } from '@/modules/sentinel-workspace/ticket-sections';
import { useAssignToMe } from '@/hooks/use-sentinel-assign-to-me';
import { canUnassignTicket, useUnassignTicket } from '@/hooks/use-sentinel-unassign';
import {
  canManageSentinelMembersAtom,
  isSentinelRoleAtom,
  resolvedSentinelSectionAtom,
} from '@/hooks/use-sentinel-routing';
import { useAtom, useAtomValue } from 'jotai';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { currentUserAtom } from '@/hooks/use-auth';
import { usePermissions } from '@/hooks/use-permissions';
import { useSentinelSettingsGate } from '@/hooks/use-sentinel-dashboard-enabled';
import {
  COMPOSER_STATUS,
  WORKSPACE_MODAL,
  selectedTicketIdAtom,
  sentinelTicketTotalAtom,
  ticketListCollapsedAtom,
  useSentinelComposer,
  useSentinelConversation,
  useSentinelTickets,
  useSubscriptionIntegrations,
} from '@/hooks/use-sentinel-workspace';
import { useTicketPresence } from '@/hooks/use-ticket-presence';
import { selectedBrandAtom } from '@/stores/sentinel-workspace.store';
import { TicketList } from '@/components/shared/ticket-list';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { TicketRowMenu } from './ticket-row-menu';
import { RefreshCw, UserMinus, UserPlus } from 'lucide-react';
import { useAutomationReplies } from '@/hooks/use-automation-replies';
import type { AutomationReplyFilter } from '@/services/automation-reply.service';
import { AutomationQueue } from './automation-review';
import { Button } from '@/components/ui/button';
import { ChatThread, ConversationHeader } from './chat-thread';
import { AiChatOverlayHost, ComposerPanel, type PendingSend } from './composer-panel';
import { DetailsPanel, DETAILS_MIN_WIDTH } from './details-panel';
import { detailsPanelInputs } from './details-panel-inputs';
import { SimilarTicketPanel } from './similar-ticket-panel';
import { OpenInHelpdeskButton } from './open-in-helpdesk-button';
import { buildThreadMessages, latestPendingActionsTurn } from './sentinel-workspace.helpers';
import {
  supportsCustomerTickets,
  supportsProviderAttachments,
  supportsProviderHandoff,
  type FeedbackVerdict,
  type RejectReason,
  type SimilarTicketMatch,
} from './types';
import { FeedbackForAiModal, RejectModal, type RejectHandoff, type RejectModalVariant } from './workspace-modals';
import type { TicketFieldValue } from '@/types';
import { FollowupsPanel } from './followups/followups-panel';
import { FollowupDrawer } from './followups/followup-drawer';
import { followupCountsQueryAtom } from './followups/followup-state';
import { TicketFollowupControl } from './followups/ticket-followup';
import { queryClientAtom } from 'jotai-tanstack-query';
import { ListIconButton, ViewPicker, type WorkspaceView } from './view-picker';

/** `?section=followups` swaps the ticket panes for the Follow-ups list, so the section survives a refresh and can be linked. */
const SECTION_PARAM = 'section';
const FOLLOWUPS_SECTION = 'followups';

function DetailsPanelSkeleton() {
  return (
    <div className="flex w-[320px] shrink-0 flex-col gap-4 border-l border-gray-500 bg-white p-4">
      <Skeleton className="h-5 w-24 rounded-md" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-32 rounded-md" />
        <Skeleton className="h-4 w-44 rounded-md" />
      </div>
      <Skeleton className="h-28 w-full rounded-2xl" />
      <Skeleton className="h-40 w-full rounded-2xl" />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-6">
      <div className="flex size-20 items-center justify-center rounded-2xl border border-gray-300 bg-white shadow-[0_2px_6px_rgba(0,0,0,0.04)]">
        <img src="/assets/brand/kim-ai-mark-mono.svg" alt="Kim AI" className="size-10" />
      </div>
      <p className="text-[13px] leading-[18px] text-text-body">Please choose a ticket to get started</p>
    </div>
  );
}

/** White bubbles like real messages — grey would vanish on this background. */
function ThreadSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex w-2/3 max-w-[560px] items-start gap-2">
        <Skeleton className="size-6 shrink-0 rounded-full border border-gray-300 bg-white" />
        <div className="flex-1 rounded-2xl border border-gray-300 bg-white p-4 shadow-[0_2px_6px_0_rgba(0,0,0,0.04)]">
          <div className="flex animate-pulse flex-col gap-2">
            <div className="h-3 w-3/4 rounded bg-gray-200" />
            <div className="h-3 w-1/2 rounded bg-gray-200" />
          </div>
        </div>
      </div>
      <div className="w-1/2 max-w-[420px] self-end rounded-2xl border border-gray-300 bg-white p-4">
        <div className="flex animate-pulse flex-col gap-2">
          <div className="h-3 w-2/3 self-end rounded bg-gray-200" />
          <div className="h-3 w-2/5 self-end rounded bg-gray-200" />
        </div>
      </div>
      <div className="flex w-2/3 max-w-[560px] items-start gap-2">
        <Skeleton className="size-6 shrink-0 rounded-full border border-gray-300 bg-white" />
        <div className="flex-1 rounded-2xl border border-gray-300 bg-white p-4 shadow-[0_2px_6px_0_rgba(0,0,0,0.04)]">
          <div className="h-3 w-3/5 animate-pulse rounded bg-gray-200" />
        </div>
      </div>
    </div>
  );
}

function ComposerSkeleton() {
  return (
    <div className="absolute inset-x-0 bottom-0 p-4">
      <div className="h-[52px] w-full animate-pulse rounded-2xl border border-gray-300 bg-white" />
    </div>
  );
}

// The same shell it is about to become, shown while the settings call decides.
function WorkspaceSkeleton({ canEdit, hasTicket }: { canEdit: boolean; hasTicket: boolean }) {
  const noop = () => {};
  return (
    <div className="flex h-full w-full overflow-hidden bg-white">
      <TicketList items={[]} selectedId={null} onSelect={noop} isLoading onCollapse={noop} />

      {/* A floor, not min-w-0: it is what makes the details panel shrink first. */}
      <div className="flex min-w-[420px] flex-1 flex-col">
        {hasTicket ? (
          <>
            <ConversationHeader
              displayId={null}
              listCollapsed={false}
              onExpandList={noop}
              detailsCollapsed={false}
              onExpandDetails={noop}
            />
            <div className="relative flex min-h-0 flex-1 flex-col bg-gray-100">
              <div className="min-h-0 flex-1 overflow-y-auto">
                <ThreadSkeleton />
              </div>
              {canEdit && <ComposerSkeleton />}
            </div>
          </>
        ) : (
          <EmptyState />
        )}
      </div>

      {hasTicket && <DetailsPanelSkeleton />}
    </div>
  );
}

function ConversationError({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3">
      <p className="text-[15px] font-medium text-text-body">Unable to load this ticket</p>
      <p className="text-[13px] leading-[18px] text-gray-800">It may no longer exist or you may not have access to it.</p>
      <Button variant="outline" onClick={onBack}>
        Back to tickets
      </Button>
    </div>
  );
}

/** The whole review queue, any date: the same query the queue itself runs before a date is picked. */
const REVIEW_QUEUE: AutomationReplyFilter = { review: 'pending', outcome: 'sent' };

/** The list title: the current view as a menu, each view with its count. */
function WorkspaceViewPicker({ view, onChange }: { view: WorkspaceView; onChange: (view: WorkspaceView) => void }) {
  const { total: review } = useAutomationReplies(REVIEW_QUEUE);
  const tickets = useAtomValue(sentinelTicketTotalAtom);
  const followups = useAtomValue(followupCountsQueryAtom).data;
  return (
    <ViewPicker
      view={view}
      onChange={onChange}
      counts={{
        tickets,
        review,
        followupsNeedYou: followups && followups.drafts + followups.handoff,
        followupsLive: followups && followups.waiting + followups.held,
      }}
    />
  );
}

function EnabledSentinelWorkspace({ canEdit }: { canEdit: boolean }) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Follow-ups is a section in the URL; the other two views are local to the page.
  const [listView, setListView] = useState<Exclude<WorkspaceView, 'followups'>>('tickets');
  const view: WorkspaceView = searchParams.get(SECTION_PARAM) === FOLLOWUPS_SECTION ? 'followups' : listView;
  const changeView = (next: WorkspaceView) => {
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      if (next === 'followups') params.set(SECTION_PARAM, FOLLOWUPS_SECTION);
      else params.delete(SECTION_PARAM);
      return params;
    });
    if (next !== 'followups') setListView(next);
  };
  const viewSwitch = <WorkspaceViewPicker view={view} onChange={changeView} />;

  if (view === 'followups') return <FollowupsPanel canEdit={canEdit} viewSwitch={viewSwitch} />;
  return <TicketWorkspace canEdit={canEdit} view={view} viewSwitch={viewSwitch} />;
}

function TicketWorkspace({ canEdit, view, viewSwitch }: { canEdit: boolean; view: WorkspaceView; viewSwitch: React.ReactNode }) {
  const queryClient = useAtomValue(queryClientAtom);
  const [listCollapsed, setListCollapsed] = useAtom(ticketListCollapsedAtom);
  const [detailsCollapsed, setDetailsCollapsed] = useState(false);
  // Phones show one pane at a time: the list, or the open ticket; its details open as a sheet.
  const isMobile = useIsMobile();
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
  const [detailsWidth, setDetailsWidth] = useState(DETAILS_MIN_WIDTH);
  // Reply held while the send-time feedback modal is open.
  const [pendingSend, setPendingSend] = useState<PendingSend | null>(null);
  // True while a send or note request is in flight — blocks duplicate submits.
  const [sendBusy, setSendBusy] = useState(false);

  const tickets = useSentinelTickets();
  const {
    conversation,
    detail,
    isLoading: conversationLoading,
    error: conversationError,
    refresh: refreshConversation,
  } = useSentinelConversation();
  const connectedSubscriptions = useSubscriptionIntegrations();
  const { composer, modal, setModal, versions, sentReply, privateNotes, aiChat, refineLimitHit, refineReady, blueprintLimitHit, toolTrace, actions } =
    useSentinelComposer(conversation, detail);
  // Off in the Automation view: the ticket left open behind it is not being looked at.
  const presence = useTicketPresence(view === 'tickets' ? conversation?.id ?? null : null);
  // Only a live Gorgias ticket is written to Gorgias, which refuses a close while a required field is empty.
  const liveGorgias = detail?.ticket.mode === 'live' && detail.ticket.source_provider === 'gorgias';
  // Only a Sentinel takes an unassigned ticket ("Assign to me").
  const { role } = usePermissions();
  const isSentinel = role === 'sentinel';
  const showOwners = useAtomValue(resolvedSentinelSectionAtom) === 'all';
  // "Assign to me": a Sentinel takes a ticket nobody owns yet.
  const assignToMe = useAssignToMe(tickets.selectTicket);
  const unassignedIds = useMemo(
    () => new Set(tickets.tickets.filter((ticket) => ticket.routerState === 'unassigned').map((ticket) => ticket.id)),
    [tickets.tickets],
  );
  const canAssign = (ticketId: number) => isSentinel && unassignedIds.has(ticketId);
  // On a list row: a "⋯" menu. In the open ticket's header: a plain button.
  const assignRowMenu = (ticketId: number) =>
    canAssign(ticketId) ? (
      <TicketRowMenu
        assigning={assignToMe.pendingTicketId === ticketId}
        busy={assignToMe.pendingTicketId !== null}
        onAssignToMe={() => void assignToMe.assign(ticketId)}
      />
    ) : null;
  const assignHeaderButton = (ticketId: number) =>
    canAssign(ticketId) ? (
      <Button
        variant="outline"
        size="sm"
        disabled={assignToMe.pendingTicketId !== null}
        onClick={() => void assignToMe.assign(ticketId)}
        className="h-8 gap-1.5 px-3 text-[13px]"
      >
        <UserPlus className="size-3.5" />
        {assignToMe.pendingTicketId === ticketId ? 'Assigning…' : 'Assign to me'}
      </Button>
    ) : null;
  // "Unassign": the owner, or an administrator, gives the open ticket up. It stays open and refreshes.
  const currentUser = useAtomValue(currentUserAtom);
  const isAdmin = useAtomValue(canManageSentinelMembersAtom);
  const isSentinelRole = useAtomValue(isSentinelRoleAtom);
  const refreshOpenTicket = useCallback(() => void refreshConversation(), [refreshConversation]);
  const unassignTicket = useUnassignTicket(refreshOpenTicket);
  const canUnassign = (ticketId: number) =>
    canUnassignTicket({
      ownerId: tickets.tickets.find((ticket) => ticket.id === ticketId)?.ownerId ?? null,
      currentUserId: currentUser?.id,
      isAdmin,
      isSentinel: isSentinelRole,
    });
  const unassignHeaderButton = (ticketId: number) =>
    canUnassign(ticketId) ? (
      <Button
        variant="outline"
        size="sm"
        disabled={unassignTicket.pendingTicketId !== null}
        onClick={() => void unassignTicket.unassign(ticketId)}
        className="h-8 gap-1.5 px-3 text-[13px]"
      >
        <UserMinus className="size-3.5" />
        {unassignTicket.pendingTicketId === ticketId ? 'Unassigning…' : 'Unassign'}
      </Button>
    ) : null;

  // Built once per data change — the panel inputs walk every tool result.
  // An approval prompt carries the order it is about, so a HITL ticket shows one without a lookup tool.
  // New row objects each render would redraw every row.
  const listItems = useMemo(
    () =>
      tickets.tickets.map((ticket) => ({
        id: ticket.id,
        subject: ticket.subject,
        customerName: ticket.customerName,
        preview: ticket.preview,
        category: ticket.category,
        timestamp: ticket.lastActivityAt,
        // Only the admin "All" view names owners; elsewhere every row is the viewer's or nobody's.
        ...(showOwners ? { ownerName: ticket.ownerName } : {}),
        unread: tickets.unreadTicketIds.has(ticket.id),
      })),
    [tickets.tickets, tickets.unreadTicketIds, showOwners],
  );

  // Built once per data change — the panel inputs walk every tool result. Shared with Review and
  // Follow-ups (ticket-context-panel), so a ticket reads the same wherever it is opened.
  const panelInputs = useMemo(
    () => (conversation ? detailsPanelInputs({ conversation, detail, toolTrace, connectedSubscriptions }) : null),
    [conversation, detail, toolTrace, connectedSubscriptions],
  );
  const detailsStorefront = panelInputs?.storefront ?? null;
  const detailsSubscriptions = panelInputs?.subscriptions ?? [];
  const similarTickets = useMemo(() => panelInputs?.similarTickets ?? [], [panelInputs]);

  const pendingActionsTurn = useMemo(() => latestPendingActionsTurn(detail?.aiEventLogs ?? []), [detail?.aiEventLogs]);
  const [openSimilarTicket, setOpenSimilarTicket] = useState<SimilarTicketMatch | null>(null);
  // A match belongs to one ticket; carried onto the next it would be refused with a 403.
  useEffect(() => setOpenSimilarTicket(null), [conversation?.id]);
  const detailsTrace = panelInputs?.trace ?? [];

  // The composer floats over the thread, so the thread reserves exactly its height. Re-observed
  // per ticket because the composer unmounts while the next conversation loads.
  // Set on the scroller, not in state: a fold resizes the composer every frame, and a re-render per frame stutters.
  const composerRef = useRef<HTMLDivElement | null>(null);
  const threadScrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = composerRef.current;
    if (!node) return;
    const observer = new ResizeObserver(() => {
      if (threadScrollRef.current) threadScrollRef.current.style.paddingBottom = `${node.offsetHeight}px`;
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [canEdit, conversation?.id]);

  const threadMessages = useMemo(
    () => buildThreadMessages(conversation, sentReply, privateNotes),
    [conversation, privateNotes, sentReply],
  );

  const rejectVariant: RejectModalVariant | null =
    modal === WORKSPACE_MODAL.rejectBlueprint || modal === WORKSPACE_MODAL.rejectResponse || modal === WORKSPACE_MODAL.assign
      ? modal
      : null;

  const handleRejectConfirm = async (
    reason: RejectReason,
    providerAgentId: string | null,
    otherText: string | null,
    handoff: RejectHandoff,
  ) => {
    let succeeded = false;
    if (modal === WORKSPACE_MODAL.assign && providerAgentId !== null) {
      succeeded = await actions.assign(reason, providerAgentId);
    } else {
      succeeded = await actions.reject({
        stage: modal === WORKSPACE_MODAL.rejectResponse ? 'response' : 'blueprint',
        reason,
        otherText,
        providerAgentId,
        tags: handoff.tags,
        note: handoff.note,
      });
    }
    if (succeeded) tickets.selectTicket(null);
  };

  const handleFeedbackClose = () => {
    setModal(null);
    setPendingSend(null);
  };

  const handleFeedbackSubmit = async (
    verdict: FeedbackVerdict,
    reasons: string[],
    otherText: string | null,
    rememberScope: string | null,
    ticketFields: TicketFieldValue[],
  ) => {
    if (!pendingSend || sendBusy) return;

    setSendBusy(true);
    try {
      const feedbackRecorded = await actions.recordFeedback(
        verdict,
        reasons,
        otherText,
        rememberScope,
        pendingSend.responseId ?? undefined,
      );
      if (!feedbackRecorded) return;
      const sent = await actions.send(pendingSend.markdown, pendingSend.close, pendingSend.files, ticketFields);
      if (!sent) return;
      setModal(null);
      setPendingSend(null);
    } finally {
      setSendBusy(false);
    }
  };

  const detailsPanel = conversation ? (
    <DetailsPanel
      key={conversation.id}
      customer={conversation.customer}
      storefront={detailsStorefront!}
      subscriptions={detailsSubscriptions}
      trace={detailsTrace}
      similarTickets={{
        matches: similarTickets,
        activeConversationId: openSimilarTicket?.conversationId ?? null,
        // On a phone the details are a sheet over the conversation, where the match opens: close it first.
        onOpenTicket: (match) => {
          setMobileDetailsOpen(false);
          setOpenSimilarTicket(match);
        },
      }}
      customerTicketsFor={supportsCustomerTickets(detail?.ticket.source_provider) ? conversation.id : undefined}
      // An automation ticket: Kim sent a reply on its own in this thread.
      automated={conversation.messages.some((message) => message.isAutomated)}
      pendingActions={
        pendingActionsTurn?.pending_actions
          ? {
              actions: pendingActionsTurn.pending_actions,
              ...(canEdit && ['pending', 'partial', 'failed'].includes(pendingActionsTurn.pending_actions.status)
                ? {
                    onRun: async () => {
                      await sentinelWorkspaceService.runPostProcessing(conversation.id, pendingActionsTurn.id);
                      await refreshConversation();
                    },
                  }
                : {}),
            }
          : undefined
      }
      onCollapse={() => (isMobile ? setMobileDetailsOpen(false) : setDetailsCollapsed(true))}
      width={detailsWidth}
      onWidthChange={isMobile ? undefined : setDetailsWidth}
    />
  ) : null;

  if (view === 'automation') {
    return (
      <AutomationQueue title={viewSwitch} canEdit={canEdit} />
    );
  }

  return (
    <div className="flex h-full w-full overflow-hidden bg-white">
      {(isMobile ? tickets.selectedId === null : !listCollapsed) && (
        <TicketList
          className={isMobile ? 'w-full' : undefined}
          items={listItems}
          title={viewSwitch}
          isLoading={tickets.isLoading}
          search={{ value: tickets.search, onChange: tickets.setSearch, placeholder: 'Search by ticket ID' }}
          searchAction={<SentinelTicketSections />}
          emptyMessage={tickets.search.trim() ? 'No ticket with that number in this view.' : undefined}
          headerActions={
            <ListIconButton label="Refresh tickets" onClick={() => void tickets.refresh()} disabled={tickets.isRefreshing}>
              <RefreshCw className={tickets.isRefreshing ? 'size-[14px] animate-spin' : 'size-[14px]'} />
            </ListIconButton>
          }
          selectedId={tickets.selectedId}
          onSelect={tickets.selectTicket}
          viewersById={presence.viewersByTicket}
          rowAction={(item) => assignRowMenu(item.id)}
          paging={{ hasMore: tickets.hasMore, isFetchingNextPage: tickets.isFetchingNextPage, onLoadMore: tickets.loadMore }}
          onCollapse={() => setListCollapsed(true)}
        />
      )}

      {/* A floor, not min-w-0: it is what makes the details panel shrink first. Phones have no floor. */}
      <div
        className={cn(
          'flex flex-1 flex-col',
          isMobile ? (tickets.selectedId === null ? 'hidden' : 'min-w-0') : 'min-w-[420px]',
        )}
      >
        {conversation || conversationLoading ? (
          <>
            <ConversationHeader
              displayId={conversation?.displayId ?? null}
              listCollapsed={isMobile || listCollapsed}
              onExpandList={() => (isMobile ? tickets.selectTicket(null) : setListCollapsed(false))}
              detailsCollapsed={isMobile || detailsCollapsed}
              onExpandDetails={() => (isMobile ? setMobileDetailsOpen(true) : setDetailsCollapsed(false))}
              viewers={presence.viewers}
              actions={
                conversation ? (
                  <>
                    <OpenInHelpdeskButton ticketId={conversation.id} />
                    {assignHeaderButton(conversation.id)}
                    {unassignHeaderButton(conversation.id)}
                    {canEdit && (
                      <TicketFollowupControl
                        key={conversation.id}
                        ticketId={conversation.id}
                        customerName={conversation.customer.name}
                      />
                    )}
                  </>
                ) : null
              }
            />
            <div className="relative flex min-h-0 flex-1 flex-col bg-gray-100">
              <div ref={threadScrollRef} className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
                {conversationLoading || !conversation ? (
                  <ThreadSkeleton />
                ) : (
                  <ChatThread messages={threadMessages} />
                )}
              </div>

              {canEdit && !conversation && conversationLoading && <ComposerSkeleton />}
              {canEdit && conversation && (
                <>
                  {/* onInput bubbles up from whichever field the composer is showing. */}
                  <div
                    ref={composerRef}
                    onInput={presence.markTyping}
                    className="absolute inset-x-0 bottom-0 flex max-h-full flex-col p-4"
                  >
                    {/* Masked so the frosting only exists at the bottom and fades out going up. */}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-gray-100 to-transparent backdrop-blur-[3px] [mask-image:linear-gradient(to_top,black_45%,transparent)]" />
                    <div className="relative flex min-h-0 flex-col">
                      <ComposerPanel
                        composer={composer}
                        actions={actions}
                        aiChat={aiChat}
                        refineLimitHit={refineLimitHit}
                        blueprintLimitHit={blueprintLimitHit}
                        sendBusy={sendBusy}
                        setSendBusy={setSendBusy}
                        onSetModal={setModal}
                        attachmentsEnabled={
                          detail?.ticket.mode === 'live' && supportsProviderAttachments(detail.ticket.source_provider)
                        }
                        onSend={(markdown, close, responseId, files) => {
                          setPendingSend({ markdown, close, responseId, files });
                          setModal(WORKSPACE_MODAL.feedback);
                        }}
                      />
                    </div>
                  </div>
                  {/* Outside the floating composer: an overlay nested there would be clipped to it. */}
                  <AiChatOverlayHost
                    actions={actions}
                    aiChat={aiChat}
                    versions={versions}
                    refineLimitHit={refineLimitHit}
                    refineReady={refineReady}
                  />
                </>
              )}
              {/* Not behind canEdit: reading a past ticket needs no reply rights. */}
              {openSimilarTicket && conversation && (
                <SimilarTicketPanel
                  key={openSimilarTicket.conversationId}
                  ticketId={conversation.id}
                  match={openSimilarTicket}
                  onClose={() => setOpenSimilarTicket(null)}
                />
              )}
            </div>
          </>
        ) : conversationError ? (
          <ConversationError onBack={() => tickets.selectTicket(null)} />
        ) : (
          <EmptyState />
        )}
      </div>

      {!conversation && conversationLoading && !detailsCollapsed && !isMobile && <DetailsPanelSkeleton />}
      {conversation && !detailsCollapsed && !isMobile && detailsPanel}
      {isMobile && conversation && (
        <Sheet open={mobileDetailsOpen} onOpenChange={setMobileDetailsOpen}>
          {/* The panel has its own close control at the same corner; the sheet's own would sit on top of it. */}
          <SheetContent side="right" showClose={false} className="w-[360px] max-w-full gap-0 overflow-y-auto p-0">
            <SheetTitle className="sr-only">Ticket details</SheetTitle>
            {/* The sheet sets the width: the desktop panel's own width and floor would overflow it. */}
            <div className="flex min-h-0 flex-1 [&>*]:w-full! [&>*]:min-w-0! [&>*]:border-l-0">{detailsPanel}</div>
          </SheetContent>
        </Sheet>
      )}

      {rejectVariant && conversation && (
        <RejectModal
          open
          variant={rejectVariant}
          ticketId={conversation.id}
          offersProviderAgent={detail?.ticket.mode === 'live' && supportsProviderHandoff(detail.ticket.source_provider)}
          afterError={composer.status === COMPOSER_STATUS.generationError}
          onClose={() => setModal(null)}
          onConfirm={handleRejectConfirm}
        />
      )}
      {/* The header's "View details" opens a follow-up here, not only in the Follow-ups section. */}
      <FollowupDrawer onChanged={() => void queryClient.invalidateQueries({ queryKey: ['sentinel', 'followups'] })} />
      <FeedbackForAiModal
        open={modal === WORKSPACE_MODAL.feedback}
        closeTicketFields={pendingSend?.close === true && liveGorgias}
        onClose={handleFeedbackClose}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
}

export function SentinelWorkspace() {
  const user = useAtomValue(currentUserAtom);
  const { can } = usePermissions();
  const canEdit = can('edit', 'sentinel-workspace');
  const [brand, setBrand] = useAtom(selectedBrandAtom);
  const [selectedTicketId, setSelectedTicketId] = useAtom(selectedTicketIdAtom);
  const settings = useSentinelSettingsGate();

  const parsedAccountId = user?.accountId == null ? null : Number(user.accountId);
  const activeAccountId = parsedAccountId !== null && Number.isFinite(parsedAccountId) ? parsedAccountId : null;
  const activeBrand = brand?.id === activeAccountId ? brand : null;

  useEffect(() => {
    if (activeAccountId === null) {
      setSelectedTicketId(null);
      return;
    }
    // Nothing saved (first visit after login) means the account the user is logged into.
    if (!brand || brand.id !== activeAccountId) {
      setBrand({ id: activeAccountId, name: user?.accountName ?? brand?.name ?? '' });
      // Only a real account switch leaves a ticket from another account open.
      if (brand) setSelectedTicketId(null);
    }
  }, [activeAccountId, brand, setBrand, setSelectedTicketId, user?.accountName]);

  if (!activeBrand) return <WorkspaceSkeleton canEdit={canEdit} hasTicket={selectedTicketId !== null} />;

  if (settings.state === 'error') {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3">
        <p className="text-[15px] font-medium text-text-body">Unable to load Sentinel settings</p>
        <p className="text-[13px] leading-[18px] text-gray-800">No tickets were loaded. Try again when the connection is restored.</p>
        <Button variant="outline" onClick={settings.retry}>
          Retry
        </Button>
      </div>
    );
  }

  if (settings.state === 'loading') return <WorkspaceSkeleton canEdit={canEdit} hasTicket={selectedTicketId !== null} />;

  if (!settings.enabled) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3">
        <p className="text-[15px] font-medium text-text-body">Sentinel is turned off for this brand</p>
        <p className="text-[13px] leading-[18px] text-gray-800">
          An administrator can turn it back on in Settings &rarr; Sentinel Workspace. Existing tickets are kept.
        </p>
      </div>
    );
  }

  return <EnabledSentinelWorkspace canEdit={canEdit} />;
}
